import { appState } from '../core/state';
import { t } from '../ui/lang';
import { navigate } from '../core/router';
import { isAggregatorFarm, isDuoFarm, isCloudPhoneFarm, isAdspowerFarm } from '../core/farm-kind';
import { makeDeviceCard, deviceNameHtml } from '../devices/device-card';
import { createDeviceGrid, cardSizeControlsHtml, wireCardSizeControls, applyCardSizeVars, invalidateDeviceGridCache, seedDeviceGridCache, diffDeviceGrid, reconcileGrid, } from '../devices/device-grid';
import { sortBySavedOrder, wireGridDragReorder, isReordering } from '../farms/device-order';
import { stopInlineStream, inlineStreams } from '../stream/inline-stream';
import { deviceGlyphSvg } from '../stream/stream-controls-shared';
import { fetchDuoDevices, makeDuoCardForBoard, isDuoConnected, } from '../farms/duo-devices';
import { fetchGeelarkDevices, makeGeelarkCardForBoard, isGeelarkConnected, } from '../farms/geelark-devices';
import { fetchAdspowerDevices, makeAdspowerCardForBoard, } from '../farms/adspower-devices';
import { loadWorkspace, getWorkspaceSnapshot, workspaceUdids, } from './workspace-store';
// ════════════════════════════════════════════════════════════════════════════════
//  MY DEVICES — the operator's personal cross-farm board
// ════════════════════════════════════════════════════════════════════════════════
//
// One flat grid holding every device the operator took into work, whichever farm it
// lives on, so they no longer keep a browser tab open per farm and switch between them.
// Flat on purpose: grouping by farm gave each farm its own row of cards, so a handful of
// phones spread over four farms filled four rows instead of one. The owning farm is
// shown muted in front of each device's name instead.
//
// The board composes nothing itself — that is the Config page. Here it only renders.
//
// Cards, size controls, scroll arrows and the poll-driven diff/patch all come from
// devices/device-grid.ts, the same engine the farm grids use; cloud cards come from the
// duo / GeeLark builders, so a phone looks identical wherever it is shown.
// ════════════════════════════════════════════════════════════════════════════════
const SCOPE = 'workspace';
const REFRESH_MS = 30000;
let refreshTimer = null;
const cloudCache = new Map();
function stopWorkspaceRefresh() {
    if (refreshTimer !== null) {
        clearInterval(refreshTimer);
        refreshTimer = null;
    }
}
function startWorkspaceRefresh() {
    stopWorkspaceRefresh();
    refreshTimer = window.setInterval(() => {
        if (!document.getElementById('workspace-grid')) {
            stopWorkspaceRefresh();
            return;
        }
        void loadWorkspace().then(async () => {
            await refreshCloudDevices();
            if (!isReordering())
                buildWorkspace();
        });
    }, REFRESH_MS);
}
function farmById() {
    return new Map(getWorkspaceSnapshot().farms.map((f) => [f.id, f]));
}
/**
 * The board's contents, in the operator's saved order. Claims are the source of truth;
 * a device is drawn from the live snapshot (relay), the cloud cache (duo / GeeLark), or
 * as a placeholder when neither reports it any more.
 */
function boardEntries() {
    const farms = farmById();
    const entries = getWorkspaceSnapshot().claims.map((c) => ({
        udid: c.udid,
        farm: farms.get(c.farmId),
        relay: appState.allDevices.get(c.udid),
        cloud: cloudCache.get(c.udid),
    }));
    // The board mixes farms, so it keeps an arrangement of its own rather than borrowing
    // any single farm's. New claims land at the end (sortBySavedOrder appends unknowns).
    return sortBySavedOrder(SCOPE, entries, (e) => e.udid);
}
/** Relay devices on the board — what the shared poll diff snapshots. */
function relayDevices(entries) {
    return entries.map((e) => e.relay).filter((d) => !!d);
}
/** Scope key for the diff cache: the whole board skeleton, so a claim added or a cloud
 *  card arriving counts as a layout change even when no relay card's own fields moved. */
function scopeKey(entries) {
    return `${SCOPE}:${entries.map((e) => `${e.udid}/${e.farm?.id ?? ''}/${e.cloud ? 1 : 0}/${e.relay ? 1 : 0}`).join(',')}`;
}
function cardOptsFor(e) {
    return {
        ...(e.farm && isAggregatorFarm(e.farm.farm_kind) ? { aggregatorFarmId: e.farm.id } : {}),
        ...(e.farm ? { farmLabel: e.farm.name } : {}),
    };
}
/** Relay-card options resolved from a device alone — what the poll's patch path needs. */
function relayCardOpts() {
    const farms = farmById();
    return (d) => {
        const farm = d.farmId != null ? farms.get(d.farmId) : undefined;
        return {
            ...(farm && isAggregatorFarm(farm.farm_kind) ? { aggregatorFarmId: farm.id } : {}),
            ...(farm ? { farmLabel: farm.name } : {}),
        };
    };
}
// ── Cloud phones ────────────────────────────────────────────────────────────────
/**
 * Refresh the cloud cache for every cloud farm represented on the board, using the same
 * per-farm endpoint the farm grid uses (it is already scoped to this user, and served
 * from the server's sync registry — no provider call, which matters at DuoPlus' 1 QPS).
 */
async function refreshCloudDevices() {
    const { farms, claims } = getWorkspaceSnapshot();
    const cloudFarms = farms.filter((f) => isCloudPhoneFarm(f.farm_kind) || isAdspowerFarm(f.farm_kind));
    if (cloudFarms.length === 0) {
        cloudCache.clear();
        return;
    }
    const claimed = new Set(claims.map((c) => c.udid));
    const next = new Map();
    await Promise.all(cloudFarms.map(async (f) => {
        const { devices } = isDuoFarm(f.farm_kind)
            ? await fetchDuoDevices(f.id)
            : isAdspowerFarm(f.farm_kind)
                ? await fetchAdspowerDevices(f.id)
                : await fetchGeelarkDevices(f.id);
        for (const d of devices)
            if (claimed.has(d.udid))
                next.set(d.udid, d);
    }));
    cloudCache.clear();
    for (const [udid, d] of next)
        cloudCache.set(udid, d);
}
// ── Rendering ───────────────────────────────────────────────────────────────────
/**
 * A claimed device nothing reports any more — a revoked phone, a farm whose agent is
 * down, a cloud phone powered off. Kept on the board on purpose: it must be visible as
 * gone rather than quietly vanishing. It carries no control of its own; the Config page
 * lists it (ticked, marked absent) so it can still be released.
 */
function makeMissingCard(e) {
    const card = document.createElement('div');
    card.className = 'device-card offline device-card--missing';
    card.dataset['udid'] = e.udid;
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot status-dot--red"></span>
            <span class="device-name-wrap">
                ${deviceNameHtml(e.udid, e.udid, e.farm?.name)}
            </span>
        </div>
        <div class="device-card__player">
            <div class="device-player-placeholder">
                <span>${deviceGlyphSvg()}</span>
                <strong>${t('workspace.not_reported_card')}</strong>
                <span class="device-missing-hint">${t('workspace.not_reported_hint')}</span>
            </div>
        </div>
    `;
    return card;
}
function gridItemFor(e) {
    if (e.relay) {
        // makeDeviceCard re-attaches an in-progress inline stream, so rebuilding is free.
        return { udid: e.udid, preserve: false, make: () => makeDeviceCard(e.relay, cardOptsFor(e)) };
    }
    if (e.cloud && e.farm) {
        const farmId = e.farm.id;
        const label = e.farm.name;
        if (isAdspowerFarm(e.farm.farm_kind)) {
            // No live screen on an AdsPower card, so it is safe to rebuild every refresh.
            return { udid: e.udid, preserve: false, make: () => makeAdspowerCardForBoard(e.cloud, farmId, label) };
        }
        const duo = isDuoFarm(e.farm.farm_kind);
        return {
            udid: e.udid,
            // A live embedded provider screen: rebuilding the card reloads its iframe
            // and resets the remote-control session.
            preserve: duo ? isDuoConnected(e.udid) : isGeelarkConnected(e.udid),
            make: () => (duo
                ? makeDuoCardForBoard(e.cloud, farmId, label)
                : makeGeelarkCardForBoard(e.cloud, farmId, label)),
        };
    }
    return { udid: e.udid, preserve: false, make: () => makeMissingCard(e) };
}
/** Reconcile the board to the current claims + live state. */
function buildWorkspace() {
    const grid = document.getElementById('workspace-grid');
    if (!grid)
        return;
    const entries = boardEntries();
    if (entries.length === 0) {
        grid.innerHTML = `<p class="empty">${t('workspace.empty', { config: `<strong>${t('workspace.config')}</strong>` })}</p>`;
        seedDeviceGridCache(scopeKey(entries), []);
        return;
    }
    reconcileGrid(grid, entries.map(gridItemFor), 
    // A cloud phone briefly missing from one provider poll keeps its connected card.
    (udid) => isDuoConnected(udid) || isGeelarkConnected(udid));
    seedDeviceGridCache(scopeKey(entries), relayDevices(entries));
}
export async function renderWorkspace() {
    // Any explicit (non-poll) render builds a fresh board; drop the poll cache so the
    // next poll diffs against this DOM, not a stale snapshot.
    invalidateDeviceGridCache();
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    pageTitle.textContent = t('workspace.title');
    content.innerHTML = `
        <div class="users-toolbar">
            <button type="button" class="add-user-btn" id="ws-config-btn">${t('workspace.config')}</button>
        </div>
        <div class="farm-devs-header">
            ${cardSizeControlsHtml()}
        </div>
    `;
    // Composing the board lives entirely on the Config page, so the device cards here
    // carry no workspace controls of their own.
    document.getElementById('ws-config-btn').addEventListener('click', () => {
        navigate({ page: 'workspace', config: true });
    });
    const { wrap, grid, updateArrows } = createDeviceGrid({ id: 'workspace-grid' });
    content.appendChild(wrap);
    applyCardSizeVars(grid);
    wireCardSizeControls(grid, updateArrows);
    // The board mixes farms, so it is reorderable under its own arrangement.
    wireGridDragReorder(grid, SCOPE);
    await loadWorkspace();
    if (!document.body.contains(grid))
        return; // navigated away while fetching
    buildWorkspace();
    updateArrows();
    // Cloud phones arrive a beat later; the board reconciles them in without disturbing
    // the relay cards already on it.
    void refreshCloudDevices().then(() => {
        if (!document.body.contains(grid))
            return;
        buildWorkspace();
        updateArrows();
    });
    startWorkspaceRefresh();
}
// ── Live updates ────────────────────────────────────────────────────────────────
/**
 * Device-list poll hook, mirroring the farm grid's: patch in place when only per-card
 * state moved, reconcile the board when the set of cards actually changed.
 */
export function onWorkspaceDevicesChange() {
    const grid = document.getElementById('workspace-grid');
    if (!grid)
        return;
    // A drag-reorder is live: rebuilding now would move DOM nodes out from under the
    // pointer and snap the card back. The next poll after dragend repaints.
    if (isReordering())
        return;
    const entries = boardEntries();
    if (diffDeviceGrid(scopeKey(entries), relayDevices(entries), grid, relayCardOpts()) === 'patched')
        return;
    buildWorkspace();
}
/**
 * A claim changed (the Config page). Removing a device from the board also closes any
 * live stream it still holds — otherwise the session would keep the device occupied
 * with no card left to release it.
 */
window.addEventListener('workspace-change', (ev) => {
    const detail = ev.detail;
    if (!detail.inWorkspace && inlineStreams.has(detail.udid))
        stopInlineStream(detail.udid);
    // Keep the cached snapshot's claim list in step with the store's udid set, so a
    // rebuild before the next refresh already reflects the change.
    const snap = getWorkspaceSnapshot();
    snap.claims = snap.claims.filter((c) => workspaceUdids.has(c.udid));
});
